import React, { useMemo, useContext, createContext, useCallback } from 'react';
import { HTTPError } from 'clients/HTTPClient';
import {
    TEAM_FAILED,
    TEAM_REQUESTED,
    TEAM_SUCCESS,
    SEND_INVITE_REQUESTED,
    SEND_INVITE_SUCCESS,
    SEND_INVITE_FAILED,
    DELETE_INVITE_REQUESTED,
    DELETE_INVITE_SUCCESS,
    DELETE_INVITE_FAILED,
    DELETE_ORGANIZATION_MEMBER_REQUESTED,
    DELETE_ORGANIZATION_MEMBER_SUCCESS,
    DELETE_ORGANIZATION_MEMBER_FAILED,
} from './constants';
import unauthorized from '../unauthorized';
import useReducerWithMiddleware from '../useReducerWithMiddleware';
import teamReducer, { TeamState, initialTeamState } from './reducer';
import { inviteClient, organizationClient } from 'clients';
import { InviteForm } from 'clients/InviteClient';

interface TeamActions {
    getTeam: (spotPk: number, force?: boolean) => Promise<void>;
    inviteTeamMember: (form: InviteForm, orgId: number) => Promise<void>;
    deleteTeamMemberInvite: (teamMemberId: number, orgId: number) => Promise<void>;
    deleteTeamMember: (teamMemberId: number, orgId: number) => Promise<void>;
}

interface TeamContextType extends TeamState, TeamActions {}

const TeamContext = createContext<TeamContextType | null>(null);

export const TeamProvider: React.FunctionComponent<React.PropsWithChildren<object>> = ({ children }) => {
    const [state, dispatch] = useReducerWithMiddleware(teamReducer, { ...initialTeamState }, [], [unauthorized]);

    const getTeam = useCallback(
        async (orgId: number) => {
            dispatch({ type: TEAM_REQUESTED });
            try {
                const [invites, organizationMembers] = await Promise.all([
                    inviteClient.getInvites(orgId),
                    organizationClient.getOrganizationMembers(orgId),
                ]);
                const team = { invites, organizationMembers };
                dispatch({ type: TEAM_SUCCESS, payload: { team } });
            } catch (error) {
                let errorMessage;
                if (HTTPError.isHTTPError(error)) {
                    errorMessage = error.message;
                } else {
                    errorMessage = error?.toString() || 'Cannot find team';
                }
                dispatch({
                    type: TEAM_FAILED,
                    payload: { message: errorMessage },
                });
            }
        },
        // until this is fixed: https://github.com/reactjs/react.dev/issues/1889,
        // eslint-disable-next-line react-hooks/exhaustive-deps
        []
    );

    const inviteTeamMember = useCallback(
        async (teamInvite: InviteForm, orgId: number) => {
            dispatch({ type: SEND_INVITE_REQUESTED });
            try {
                await inviteClient.sendInvite({ ...teamInvite, organization: orgId });
                dispatch({ type: SEND_INVITE_SUCCESS });
                getTeam(orgId);
            } catch (error) {
                let errorMessage;
                if (HTTPError.isHTTPError(error)) {
                    errorMessage = `${error.message}: ${error.cause}`;
                } else {
                    errorMessage = error?.toString() || 'Could not add team member';
                }
                dispatch({ type: SEND_INVITE_FAILED, payload: { message: errorMessage } });
            }
        },
        // until this is fixed: https://github.com/reactjs/react.dev/issues/1889,
        // eslint-disable-next-line react-hooks/exhaustive-deps
        []
    );

    const deleteTeamMemberInvite = useCallback(
        async (inviteId: number, orgId: number) => {
            dispatch({ type: DELETE_INVITE_REQUESTED });
            try {
                await inviteClient.deleteInvite(inviteId);
                dispatch({ type: DELETE_INVITE_SUCCESS });
                getTeam(orgId);
            } catch (error) {
                let errorMessage;
                if (HTTPError.isHTTPError(error)) {
                    errorMessage = error.message;
                } else {
                    errorMessage = error?.toString() || 'Could not delete member';
                }
                dispatch({
                    type: DELETE_INVITE_FAILED,
                    payload: { message: errorMessage },
                });
            }
        },
        // until this is fixed: https://github.com/reactjs/react.dev/issues/1889,
        // eslint-disable-next-line react-hooks/exhaustive-deps
        []
    );

    const deleteTeamMember = useCallback(
        async (memberId: number, orgId: number) => {
            dispatch({ type: DELETE_ORGANIZATION_MEMBER_REQUESTED });
            try {
                await organizationClient.deleteOrganizationMember(orgId, memberId);
                dispatch({ type: DELETE_ORGANIZATION_MEMBER_SUCCESS });
                getTeam(orgId);
            } catch (error) {
                let errorMessage;
                if (HTTPError.isHTTPError(error)) {
                    errorMessage = error.message;
                } else {
                    errorMessage = error?.toString() || 'Could not delete member';
                }
                dispatch({ type: DELETE_ORGANIZATION_MEMBER_FAILED, payload: { message: errorMessage } });
            }
        },
        // until this is fixed: https://github.com/reactjs/react.dev/issues/1889,
        // eslint-disable-next-line react-hooks/exhaustive-deps
        []
    );

    const value = useMemo<TeamContextType>(() => {
        return {
            ...state,
            getTeam,
            inviteTeamMember,
            deleteTeamMemberInvite,
            deleteTeamMember,
        };
    }, [state, getTeam, inviteTeamMember, deleteTeamMemberInvite, deleteTeamMember]);

    return <TeamContext.Provider value={value}>{children}</TeamContext.Provider>;
};

export const useTeam = () => {
    const context = useContext(TeamContext);
    if (!context) {
        throw new Error('Error: useTeam should be wrapped by TeamProvider.');
    }
    return context;
};
